import { clamp, hash01, lerp, placeKey, smoothstep, valueNoise } from '@/core/rng';
import { BIOMES, type Biome, type BiomeKey } from './types';

/**
 * Ground litter as *world content* — the pebbles, twigs, clumps and deadfall
 * that live under the ankle, and the eleven-odd things you might actually
 * stop for.
 *
 * `render/nearDetail.ts` draws them, and drew them alone for a long time: the
 * whole layer was render-only, so the simulation could not see it and neither
 * could the pointer. That was fine while litter was scenery. It stops being
 * fine under Design III, whose §28 says that if a thing meaningfully exists
 * the player should be able to point at it, and whose §37 says that pointing
 * must create nothing.
 *
 * Both are cheap here, because of the one property the layer was built with:
 *
 * **The lattice is invertible.** Every property of an item — whether the cell
 * carries anything at all, where in the cell it stands, what kind it is, how
 * big, which way round — is `hash01` of the cell's *integer coordinates* and
 * nothing else. No camera quantity, no visit order, no draw order. So the
 * question "what is at this point?" is not a search through what happens to be
 * on screen: it is `floor(x / STEP)`, `floor(z / STEP)`, and a handful of
 * hashes. **Picking a pebble is cheaper than picking a tree**, which has to go
 * through its chunk's vegetation list.
 *
 * Identity is free for the same reason it is free for a tree: position *is*
 * the name (`placeKey`), the same on every machine for ever, so nothing is
 * minted and nothing is stored.
 *
 * ## What lives here and what stays in the renderer
 *
 * Here: the lattice, the hashes, the biome table, the special-patch election,
 * the wear rule, and the decision of what stands where — everything that is a
 * fact about the world.
 *
 * There: geometry, colour, sway, the camera-local disc, and the *budget*. The
 * budget matters and is deliberately excluded: the renderer thins the scatter
 * to what it can afford at the current view, so a stone that exists may not be
 * drawn this frame. **What is here and what is on screen are different
 * questions**, and this module answers the first. That is the same answer
 * `world/things.ts` gives when a promoted plant is culled by wear — the mark
 * is on the *place*.
 *
 * Note the word *budget* in that paragraph, because a defect lived in the gap
 * next to it for a while. Ground **wear** is not a budget: a camp floor packed
 * to bare earth genuinely has no sedge growing on it, and that is a fact about
 * the world, not about what the renderer can afford. The renderer knew it —
 * `nearDetail` swept the carpet and thinned the living clumps on walked ground
 * — and this module did not, so over a camp the picture drew about a fifth of
 * the clumps while the pointer went on naming every one of the missing four
 * fifths. `wornKeep` below is that rule, moved to the side of the wall it
 * belongs on, and both callers now read it from here.
 */

/** Metres between candidate cells. One item per cell, jittered inside it. */
export const STEP = 2.0;

// hash salts — distinct constants, so no two decisions ever correlate.
// Shared with the renderer, which must agree with this module exactly.
export const S_JX = 0x1a37, S_JZ = 0x2b41, S_OCC = 0x3c59, S_KIND = 0x4d6b;
export const S_LOG = 0x5e71, S_SZ = 0x6f83, S_SH = 0x7091, S_ROT = 0x81a3;
export const S_TONE = 0x92b7;
export const S_PATCH_S = 0xb4d3, S_PATCH_L = 0xc5e9, S_PATCH_G = 0xd6f1;
/** …and the three the discoverable half added. */
export const S_BARE = 0xe2f7, S_SPEC_PICK = 0xf30b, S_SPEC_ON = 0x0417;
export const S_SPECIAL = 0x1523;

/**
 * Where a cell's item stands: the cell's own middle, shoved off it by a hash
 * of the cell's integer coordinates. This — and only this — is what fixes an
 * item to the world.
 */
export const spotX = (ix: number, iz: number): number =>
  (ix + 0.5) * STEP + (hash01(ix, iz, S_JX) - 0.5) * STEP * 0.86;
export const spotZ = (ix: number, iz: number): number =>
  (iz + 0.5) * STEP + (hash01(ix, iz, S_JZ) - 0.5) * STEP * 0.86;

/** The cell a world position falls in. */
export const cellOf = (x: number, z: number): { ix: number; iz: number } =>
  ({ ix: Math.floor(x / STEP), iz: Math.floor(z / STEP) });

/**
 * The things that lie on the ground.
 *
 * Four of these are the ordinary business of the layer and seven are what
 * Griffin asked for — *"a bunch of special things that players can discover"*.
 * The difference is not in the code path, which is identical, but in how they
 * arrive: the first four are what a channel yields by default, and the rest
 * only appear where the ground has a **speciality** (see `SPECIAL` and the
 * patch election below). A fern is not a rare item; a fern *bank* is a place.
 */
export type LitterKind =
  | 'stone' | 'stick' | 'clump' | 'log'
  | 'shell' | 'bone' | 'fern' | 'flower' | 'mushroom' | 'moss' | 'seedhead';

// The first four indices are unchanged on purpose: `render/nearDetail.ts`
// caches kind by index and `world/carried.ts` has stored kind strings in every
// save ever written. Renumbering them would silently re-kind the world.
export const K_STONE = 0, K_STICK = 1, K_CLUMP = 2, K_LOG = 3;
export const K_SHELL = 4, K_BONE = 5, K_FERN = 6, K_FLOWER = 7;
export const K_MUSHROOM = 8, K_MOSS = 9, K_SEEDHEAD = 10;
export const KIND_NAME: readonly LitterKind[] = [
  'stone', 'stick', 'clump', 'log',
  'shell', 'bone', 'fern', 'flower', 'mushroom', 'moss', 'seedhead',
];
export const KIND_COUNT = KIND_NAME.length;

/**
 * Which of the three SPEC weight channels each kind rides.
 *
 * 0 = stone, 1 = dead litter, 2 = living growth. The SPEC table has exactly
 * these three columns and gains no fourth: a special *replaces* the ordinary
 * item its channel would have grown, so adding seven kinds added no density at
 * all. That is the property that made the change safe — the budget, the caps
 * and every biome's total are untouched by the new vocabulary, and a mushroom
 * costs precisely one clump.
 */
export const CHANNEL: readonly number[] = [
  0, 1, 2, 1,          // stone, stick, clump, log
  0, 1, 2, 2, 2, 2, 2, // shell, bone, fern, flower, mushroom, moss, seedhead
];

/**
 * Whether a kind is alive — which decides whether walked ground sweeps it.
 * Dead debris stays in a camp, because a camp's floor has debris.
 */
export const LIVING: readonly boolean[] = [
  false, false, true, false,
  false, false, true, true, true, true, true,
];

/**
 * How eagerly each kind spends the render budget; also its occupancy weight.
 *
 * The clump's 1.30 was the single loudest number in the layer and it is gone.
 * `tools/litter.ts` measured what it bought: **80% of every drawn instance on a
 * coast-forest floor was one clump shape**, 6,913 of 8,209, and a monoculture
 * of one silhouette at one size is exactly what reads as spam however varied
 * its colour is. At 1.00 the clump is still the widest thing in the layer and
 * still the thing that carries the middle distance; it simply no longer gets a
 * thirty per cent head start over everything else competing for the same
 * ground. The stone and the stick come up to meet it for the same reason.
 *
 * The seven specials inherit their channel's weight rather than carrying one
 * of their own — see `specialise`: occupancy is decided BEFORE the conversion,
 * so a special stands exactly where its channel's ordinary item would have.
 */
export const BOOST = [
  1.00, 0.98, 1.00, 1.7,
  1.00, 0.98, 1.00, 1.00, 1.00, 1.00, 1.00,
] as const;
/** Share of would-be sticks that are instead a fallen log, in woody country. */
export const LOG_CHANCE = 0.014;
/** The most litter any ground may carry, as a per-cell occupancy probability. */
export const A_MAX = 0.92;
/** No litter in the surf: dry land clamps to 0.8 m, so this clears the waterline. */
export const DRY_FOOT = 1.0;
/** Diorama scale — see the note in `render/nearDetail.ts`. */
export const MODEL = 2.4;

/**
 * Past this eye distance the litter layer switches off entirely, and past it
 * litter also stops being *pointable*.
 *
 * Measured rather than guessed, twice. It was 780 m when the layer drew
 * nothing but the sub-metre scatter: at 800 m a 0.2 m pebble covers about a
 * third of a pixel, so past it there was nothing to draw *or* to mean.
 * `render/nearDetail.ts` now carries a landmark band — fallen logs, fists of
 * stone, the big clumps — much further than the scatter, and the arithmetic
 * for those is different: this camera puts an 8 m log at ~5.7 px from 1.5 km,
 * which still reads and is still worth pointing at. Anything smaller is
 * protected by the pick's own pixel gate in `main.ts` (under ~3 px is not a
 * signal), so raising this ceiling cannot let a pebble outrank a settlement
 * from the air — the gate, not this constant, is what defends that.
 *
 * Note what this does *not* change: `litterAt` stays camera-free and still
 * answers "what is here" at any range. Existence is a fact about the world;
 * pointability is a fact about a screen, and only the second one is allowed
 * to care where the eye is.
 */
export const POINTABLE_FAR = 1500;

export interface Litter {
  /** How much of the ground carries anything at all, 0..1. */
  amount: number;
  /** Relative weights of stone, dead litter and small living growth. */
  stone: number; litter: number; growth: number;
}

/**
 * Stony ground gets pebbles, wet green ground gets small growth, dry ground
 * gets bare litter — the same reading `groundColour` makes of the same biome,
 * in objects rather than in tint. Rain tilts all three afterwards, so one
 * biome's dry shoulder and its damp hollow do not litter alike.
 *
 * The four woody rows were re-weighted against a measurement rather than by
 * feel. `tools/litter.ts` printed a coast-forest floor as 80% clump / 16% stick
 * / 3% stone, and the arithmetic that produced it is visible in this table: at
 * `growth 0.56` and a rain multiplier that reaches 1.75 in wet country, the
 * living channel outweighed the other two together by four to one. A real
 * closed-canopy floor is the opposite — needle mat, fallen wood, and the green
 * confined to the gaps — so growth comes down and dead litter goes up in
 * exactly the four biomes with a canopy over them. The open rows are untouched:
 * grassland genuinely is mostly growth and always was.
 *
 * `litter` is deliberately no longer the majority of any forest floor: four
 * shapes cannot draw a mat of needles, so the mat is the ground's own colour
 * and this table spends its budget on the things that stand *proud* of it.
 */
export const SPEC: Record<string, Litter> = {
  ocean:     { amount: 0.00, stone: 0.00, litter: 0.00, growth: 0.00 },
  beach:     { amount: 0.50, stone: 0.80, litter: 0.16, growth: 0.04 },
  marsh:     { amount: 0.66, stone: 0.06, litter: 0.14, growth: 0.80 },
  riparian:  { amount: 0.90, stone: 0.20, litter: 0.38, growth: 0.52 },
  redwood:   { amount: 0.92, stone: 0.10, litter: 0.46, growth: 0.40 },
  oak:       { amount: 0.80, stone: 0.16, litter: 0.40, growth: 0.44 },
  chaparral: { amount: 0.62, stone: 0.38, litter: 0.28, growth: 0.34 },
  grassland: { amount: 0.62, stone: 0.12, litter: 0.14, growth: 0.78 },
  scrub:     { amount: 0.64, stone: 0.26, litter: 0.24, growth: 0.56 },
  desert:    { amount: 0.44, stone: 0.56, litter: 0.30, growth: 0.14 },
  conifer:   { amount: 0.88, stone: 0.18, litter: 0.46, growth: 0.38 },
  alpine:    { amount: 0.68, stone: 0.88, litter: 0.06, growth: 0.10 },
};
export const FALLBACK = SPEC.grassland;

/**
 * What each biome is *known for* — the pool a patch of that ground draws its
 * speciality from.
 *
 * This is the whole discovery half of the layer, and it is a table rather than
 * a rule because the point is that ground has a character you can learn. A god
 * who has walked a coast forest knows to look in the hollows for mushrooms; one
 * who has walked the strand knows the shells are there and the bones are not.
 * Law 17 — knowledge of the world is part of a god's strength — is the reason
 * this is not seven hashes sprayed evenly over the island.
 *
 * Order matters only in that it is fixed: the pick is `floor(hash × length)`,
 * so re-ordering a row moves that biome's specialities around. Adding to the
 * END of a row is safe.
 */
export const SPECIAL: Record<string, readonly LitterKind[]> = {
  ocean:     [],
  beach:     ['shell', 'bone'],
  marsh:     ['shell', 'seedhead', 'fern'],
  riparian:  ['fern', 'mushroom', 'flower', 'moss'],
  redwood:   ['fern', 'mushroom', 'moss'],
  oak:       ['mushroom', 'fern', 'flower'],
  chaparral: ['flower', 'seedhead', 'bone'],
  grassland: ['flower', 'seedhead', 'bone'],
  scrub:     ['seedhead', 'flower', 'bone'],
  desert:    ['bone', 'seedhead'],
  conifer:   ['mushroom', 'fern', 'moss'],
  alpine:    ['flower', 'moss', 'bone'],
};

/** Kind name → index, for the tables above. Built once. */
const KIND_INDEX: Record<string, number> = {};
KIND_NAME.forEach((k, i) => { KIND_INDEX[k] = i; });
const SPECIAL_IDX: Record<string, readonly number[]> = {};
for (const b of Object.keys(SPECIAL)) SPECIAL_IDX[b] = SPECIAL[b].map((k) => KIND_INDEX[k]);

/**
 * Metres of the speciality lattice — how big one flush, bank or scatter is.
 *
 * 24 m is deliberately between the two grains already in the layer: bigger than
 * the 13–21 m patch fields that decide *how much* is here, so a speciality is
 * not chopped up by them, and far smaller than a region cell, so one biome
 * carries many different specialities rather than being uniformly one thing.
 * At a walking pace a patch is about twenty seconds across, which is long
 * enough to read as somewhere rather than as an item.
 */
export const PATCH = 24;

/**
 * Share of patches that are a *flush* — a place where the speciality is the
 * ground's dominant feature rather than a thing you notice twice.
 *
 * The rest of the world still carries the speciality at `AMBIENT`, which is
 * what keeps a fern from switching on at a line on the ground. Measured
 * against `tools/litter.ts`'s 200 m walk: at these numbers a walk in coast
 * forest meets a handful of mushrooms most of the time and a real flush of them
 * every few hundred metres, which is the shape of a thing worth telling
 * somebody about.
 */
export const PATCH_RATE = 0.085;
/** Share of a flush's matching-channel items that become the speciality. */
export const PATCH_STRONG = 0.62;
/** …and everywhere else, so specialities scatter rather than switch on. */
export const AMBIENT = 0.028;

/**
 * The strengths a patch is ever at, indexed by the CODE everything passes
 * around — 0 nothing, 1 ambient, 2 flush.
 *
 * A code rather than the number itself, because of the one thing this layer
 * must not do. `render/nearDetail.ts` caches a cell's answer in typed arrays,
 * and the strength was cached as `round(p × 255)`: the renderer then
 * specialised against 158/255 = 0.619608 while `litterAt` used 0.62, so a cell
 * whose `S_SPECIAL` hash fell in that sliver was DRAWN as a clump and NAMED as
 * a fern. Measured at 40 of 182,477 items — small, silent, and exactly the
 * class of defect this module's header claims is impossible by construction.
 *
 * A small integer survives a `Uint8Array` unchanged, so the two readers cannot
 * come apart however the strengths are re-tuned; and adding a third strength is
 * a row in this table rather than a second quantisation to get wrong.
 */
export const SPECIAL_P: readonly number[] = [0, AMBIENT, PATCH_STRONG];
export const P_NONE = 0, P_AMBIENT = 1, P_FLUSH = 2;

/**
 * How open the ground is here, 0..1 — and the reason there is bare earth at
 * all.
 *
 * Before this the layer had no concept of empty ground: `tools/litter.ts`
 * measured a coast-forest floor at **94.3% of lattice cells occupied**, 2,357
 * items per 100 m², and 14% of 4 m tiles inside the drawn disc holding nothing.
 * Uniform density at that level is not scatter, it is a carpet made of objects,
 * and no amount of variety inside it reads as anything but noise.
 *
 * One smooth field at 34 m fixes it, and pushed through a hard contrast curve
 * rather than used raw: a straight multiply by noise gives everywhere a bit
 * less, which is uniformly thinner and just as uniform. `smoothstep(0.34, 0.72)`
 * of value noise puts about a third of the world near the floor and about a
 * third near full, so glades and drifts both exist and the ground between them
 * is genuinely between them.
 *
 * The floor is 0.10 rather than 0 on purpose. Zero would make a glade a
 * perfectly clean disc of nothing, and nothing in a landscape is a disc — the
 * same lesson `world/wear.ts` records about hard thresholds on smooth fields.
 */
const BARE_SCALE = 34;
const BARE_LO = 0.34, BARE_HI = 0.72, BARE_FLOOR = 0.10;
export const openness = (px: number, pz: number): number =>
  lerp(BARE_FLOOR, 1,
       smoothstep(BARE_LO, BARE_HI, valueNoise(px / BARE_SCALE, pz / BARE_SCALE, S_BARE)));

/** What the renderer and the picker both need to know about a cell's ground. */
export interface LitterCell {
  /** Occupancy probability before any render budget, 0..A_MAX. */
  amount: number;
  /** Kind thresholds over 0..255: below t1 is stone, below t2 is stick. */
  t1: number; t2: number;
  /** Woody country — the only place a stick may instead be a fallen log. */
  woody: boolean;
  /** This ground's speciality as a kind index, or −1 for ordinary ground. */
  special: number;
  /** How much of the matching channel it takes, as a `SPECIAL_P` code. */
  strength: number;
}

/** The minimum a site must supply. `Region.siteAt` satisfies it. */
export interface LitterSite { land: boolean; biome: string; rain: number }

/**
 * The speciality of the ground at a point: which kind, and how strongly.
 *
 * Two hashes of the 24 m patch's own integer coordinates and nothing else, so
 * it is invertible exactly as the rest of the lattice is. The strength comes
 * back as a `SPECIAL_P` code rather than as the number, which is what makes
 * "the renderer and the pointer compute it independently and cannot disagree"
 * true rather than aspirational — see `SPECIAL_P` for the sliver of cells that
 * disagreed while it was a float. Exported because `tools/litter.ts` maps the
 * specialities over the island to check that a walk meets them at the rate the
 * constants above claim.
 */
export function specialAt(site: LitterSite, px: number, pz: number):
  { special: number; strength: number } {
  const pool = SPECIAL_IDX[site.biome];
  if (!pool || pool.length === 0) return { special: -1, strength: P_NONE };
  const qx = Math.floor(px / PATCH), qz = Math.floor(pz / PATCH);
  const pick = hash01(qx, qz, S_SPEC_PICK);
  const special = pool[Math.min(pool.length - 1, Math.floor(pick * pool.length))];
  const flush = hash01(qx, qz, S_SPEC_ON) < PATCH_RATE;
  return { special, strength: flush ? P_FLUSH : P_AMBIENT };
}

/**
 * What the ground at one spot is made of, before anything is drawn.
 *
 * Where things gather. A uniform scatter of small objects reads as confetti;
 * real ground keeps most of itself bare and puts its gravel in aprons, its
 * deadfall in drifts and its green in damp hollows. Three smooth fields of
 * roughly fifteen to twenty metres do that, and because they multiply the
 * three weights *separately* they decide both how much a spot carries and
 * what kind — a patch where all three are low is genuinely bare ground.
 *
 * `openness` then decides how much of the ground is *nothing at all*, which is
 * a different question and was missing entirely — see its own note.
 *
 * Shared by `render/nearDetail.ts` and by `litterAt` below, so the thing you
 * point at and the thing that is drawn cannot drift apart.
 */
export function litterCell(site: LitterSite, px: number, pz: number): LitterCell | null {
  if (!site.land) return null;
  const sp = SPEC[site.biome] ?? FALLBACK;
  if (sp.amount <= 0) return null;
  const bio = BIOMES[site.biome as BiomeKey] as Biome | undefined;

  const kS = 0.15 + 1.7 * valueNoise(px / 21, pz / 21, S_PATCH_S);
  const kL = 0.15 + 1.7 * valueNoise(px / 17, pz / 17, S_PATCH_L);
  const kG = 0.15 + 1.7 * valueNoise(px / 13, pz / 13, S_PATCH_G);
  const rain = site.rain;
  const ws = sp.stone * kS * clamp(1.22 - rain * 0.7, 0.55, 1.3);
  const wl = sp.litter * kL * clamp(1.18 - rain * 0.45, 0.7, 1.25);
  const wg = sp.growth * kG * clamp(0.42 + rain * 1.9, 0.22, 1.75)
           * (0.55 + (bio ? bio.density : 0.4) * 0.7);
  const sum = ws + wl + wg;
  if (sum <= 1e-4) return null;
  const base = sp.stone + sp.litter + sp.growth;
  const { special, strength } = specialAt(site, px, pz);

  return {
    amount: clamp(sp.amount * (sum / base) * openness(px, pz), 0, A_MAX),
    t1: Math.round((ws / sum) * 255),
    t2: Math.round(((ws + wl) / sum) * 255),
    woody: !!(bio && bio.woody),
    special,
    strength,
  };
}

/**
 * How much of the living stuff survives ground people live on, 0..1.
 *
 * The rule itself is old — `render/nearDetail.ts` has swept the grass carpet
 * and thinned the clumps on packed ground since wear landed — but it lived in
 * the renderer, which made it a *picture* rule, and the pointer therefore went
 * on naming sedge that had been trodden out of existence. Wear is sparse
 * history (law 2), not a budget: the camp floor really is bare, so the fact
 * belongs here and both callers read it from here.
 *
 * 0.8 rather than 1 because a beaten camp is not sterile, and because the
 * remaining fifth is what keeps the clearing's edge a gradient instead of a
 * cut-out. Dead debris is untouched — see `LIVING`.
 */
export const WORN_SWEEP = 0.8;
export const wornKeep = (kind: number, worn: number): number =>
  (LIVING[kind] ? 1 - clamp(worn, 0, 1) * WORN_SWEEP : 1);

/**
 * The ordinary item a channel grows, turned into this ground's speciality.
 *
 * Called AFTER occupancy, so a speciality never adds or removes an item — it
 * only changes what the item is. That single ordering decision is what let
 * seven kinds land without touching one budget constant, one per-kind cap or
 * one biome total.
 *
 * A fallen log is exempt: it is the layer's one landmark-scale object, the
 * band draws it out to a kilometre, and swapping it for a bone at the last
 * moment would make the band and the disc disagree about who owns the cell.
 *
 * `strength` is a `SPECIAL_P` code, not a probability: both readers hand this
 * function the same small integer, so the threshold either side compares
 * against is bit-for-bit the same number.
 */
export function specialise(
  base: number, special: number, strength: number, ix: number, iz: number,
): number {
  const p = SPECIAL_P[strength] ?? 0;
  if (special < 0 || p <= 0 || base === K_LOG) return base;
  if (CHANNEL[special] !== CHANNEL[base]) return base;
  return hash01(ix, iz, S_SPECIAL) < p ? special : base;
}

/**
 * The drawn size of an item in metres — width for the things that stand,
 * length for the things that lie.
 *
 * One function, called by `litterAt` for what the pointer means and by
 * `render/nearDetail.ts` for what it composes. It used to be two: the renderer
 * carried its own copy in `farSize`, and two copies of a formula that must
 * agree exactly is a bug with a delay fuse.
 */
export function litterSize(kind: number, h1: number, h2: number): number {
  switch (kind) {
    case K_STONE: return lerp(0.11, 0.42, h1 * h1) * MODEL;
    case K_STICK: return lerp(0.24, 0.90, h1) * MODEL;
    case K_CLUMP: return lerp(0.14, 0.44, h1 * h1) * MODEL * lerp(1.15, 2.1, h2);
    case K_LOG: return lerp(1.6, 3.4, h1) * MODEL;
    // a shell is the smallest thing the layer has ever grown, and it should be:
    // it is under the pick's own pixel gate from anywhere but arm's length,
    // which is exactly right for something you find by looking down
    case K_SHELL: return lerp(0.06, 0.17, h1) * MODEL;
    case K_BONE: return lerp(0.30, 0.95, h1) * MODEL;
    case K_FERN: return lerp(0.26, 0.58, h1) * MODEL * lerp(1.1, 1.6, h2);
    case K_FLOWER: return lerp(0.10, 0.26, h1) * MODEL;
    case K_MUSHROOM: return lerp(0.05, 0.16, h1) * MODEL;
    case K_MOSS: return lerp(0.18, 0.50, h1 * h1) * MODEL * lerp(1.2, 1.9, h2);
    default: return lerp(0.16, 0.42, h1) * MODEL;    // seedhead
  }
}

/** One item of ground litter, as a fact about the world. */
export interface LitterItem {
  kind: LitterKind;
  /** Where it actually stands, world metres. Not where the query landed. */
  x: number; z: number;
  /** Its own footprint in metres — what "this thing" means when acted on. */
  size: number;
  /** `placeKey` of its spot: its identity, and the same key promotion uses. */
  key: string;
}

/**
 * The litter item at a world position, or null for bare ground.
 *
 * Deliberately answers *what is here*, not *what is drawn*: no camera
 * quantity enters, so it is the same answer at any altitude, on any machine,
 * and in a headless probe. The renderer's budget may be omitting this item
 * right now, exactly as `applyWear` may have culled a promoted plant — and the
 * answer is the same in both cases, because the thing is a property of the
 * place.
 *
 * `worn` is the exception that proves the rule and the reason it is a separate
 * parameter from the budget the renderer keeps to itself: wear is *history*,
 * and history changes what is here. A caller that cannot cheaply answer "how
 * packed is this ground" may leave it out and gets the baseline, exactly as a
 * caller that cannot afford a ground sample may leave out `groundAt`.
 *
 * Pure. It writes nothing, which is Design III §37 and law 9.
 */
export function litterAt(
  x: number, z: number,
  siteAt: (wx: number, wz: number) => LitterSite,
  groundAt?: (wx: number, wz: number) => number,
  worn?: (wx: number, wz: number) => number,
): LitterItem | null {
  const { ix, iz } = cellOf(x, z);
  const px = spotX(ix, iz), pz = spotZ(ix, iz);
  const site = siteAt(px, pz);
  const cell = litterCell(site, px, pz);
  if (!cell) return null;
  // the surf guard the renderer applies, when the caller can afford the sample
  if (groundAt && groundAt(px, pz) < DRY_FOOT) return null;

  const hk = hash01(ix, iz, S_KIND) * 255;
  let k = hk < cell.t1 ? K_STONE : hk < cell.t2 ? K_STICK : K_CLUMP;
  if (k === K_STICK && cell.woody && hash01(ix, iz, S_LOG) < LOG_CHANCE) k = K_LOG;

  // occupancy, at full budget — the renderer only ever thins this — with the
  // living things swept off ground that is lived on. Both factors are applied
  // BEFORE the speciality, so a flush of mushrooms is as dense as the clumps
  // it replaced and no denser.
  const w = worn ? worn(px, pz) : 0;
  if (hash01(ix, iz, S_OCC) >= cell.amount * BOOST[k] * wornKeep(k, w)) return null;

  k = specialise(k, cell.special, cell.strength, ix, iz);

  const h1 = hash01(ix, iz, S_SZ), h2 = hash01(ix, iz, S_SH);
  return {
    kind: KIND_NAME[k], x: px, z: pz,
    size: litterSize(k, h1, h2),
    key: placeKey(px, pz),
  };
}

/**
 * Roughly what an item weighs, in kilogrammes.
 *
 * Design IV §25 prices Grasp by size, mass and attachment — pebble trivial,
 * person manageable, tree substantial, boulder expensive, mountain impossible
 * — so the loose world needs to know what it weighs. Density is deliberately
 * crude: stone is heavy for its size, wood is not, and a clump of sedge is
 * mostly air. `size` is already the item's footprint in metres, so this is a
 * volume argument with a fudge, not physics.
 */
export function litterMass(kind: LitterKind, size: number): number {
  // `size` means different things per kind, and cubing it blindly does not
  // work: for a log and a stick it is the *length*, so `size³` prices a six
  // metre log as a six metre cube. It came to eleven tonnes, and the first
  // thing a god ever tried to pick up was refused as heavier than three gods.
  // So each kind gets the volume of the shape it actually is.
  const s = Math.max(0.01, size);
  switch (kind) {
    // a rounded stone, near enough a sphere of that diameter, in rock
    case 'stone': return (Math.PI / 6) * s * s * s * 2600;
    // a cylinder about a tenth as thick as it is long, in green timber
    case 'log': return Math.PI * (s / 20) ** 2 * s * 600;
    // the same shape, much thinner
    case 'stick': return Math.PI * (s / 50) ** 2 * s * 550;
    // a bone is a hollow tube, and dry: lighter than the stick it looks like
    case 'bone': return Math.PI * (s / 16) ** 2 * s * 700;
    // a thin calcium dome, and mostly the air under it
    case 'shell': return 0.12 * s * s * s * 1500;
    // a mushroom is water in a sponge, and there is not much of it
    case 'mushroom': return 0.30 * s * s * s * 320;
    // a mat rather than a thing: shallow, and holding its own weight in water
    case 'moss': return 0.10 * s * s * s * 400;
    // a big rooted frond mass — heavier than a tussock and mostly root ball
    case 'fern': return 0.35 * s * s * s * 200;
    case 'flower': return 0.20 * s * s * s * 110;
    case 'seedhead': return 0.18 * s * s * s * 90;
    // a tussock: a low dome of leaf and root ball, and mostly air
    default: return 0.3 * s * s * s * 120;
  }
}

/**
 * How hard the ground holds on, 0..1.
 *
 * Loose litter is loose — that is what makes it litter, and it is why the
 * sub-metre world is the right place for Grasp to start. A growing plant
 * resists through its roots and tears rather than lifting cleanly (IV §22),
 * so the seven kinds added here split cleanly along that line: the things that
 * were dropped are barely attached, the things that are *growing* are the most
 * attached objects on the lattice.
 */
export function litterAttachment(kind: LitterKind): number {
  switch (kind) {
    case 'stone': return 0.25;   // bedded a little into the facet it sits on
    case 'log': return 0.35;     // settled, and usually grown over
    case 'clump': return 0.55;   // a tussock, with roots of its own
    case 'shell': return 0.05;   // lying on the strand, holding nothing
    case 'bone': return 0.12;    // half-buried at worst
    case 'mushroom': return 0.30; // the mycelium lets go, the ground does not
    case 'flower': return 0.45;
    case 'seedhead': return 0.50;
    case 'moss': return 0.65;    // a mat: it comes up in one piece or not at all
    case 'fern': return 0.72;    // the deepest root ball in the layer
    default: return 0.05;        // a twig is not attached to anything
  }
}

/**
 * The nearest litter item to a point, within `reach` metres — what a pointer
 * on the ground should resolve to.
 *
 * A ray lands on the ground, not on a 30 cm pebble, so an exact cell lookup
 * would almost always miss. This walks the few cells the reach can touch,
 * which at `STEP` = 2 m and a metre or two of reach is nine at most, and each
 * is O(1). Still cheaper than one raycast.
 */
export function litterNear(
  x: number, z: number, reach: number,
  siteAt: (wx: number, wz: number) => LitterSite,
  groundAt?: (wx: number, wz: number) => number,
  worn?: (wx: number, wz: number) => number,
): LitterItem | null {
  const r = Math.max(0, Math.ceil(reach / STEP));
  const { ix, iz } = cellOf(x, z);
  let best: LitterItem | null = null, bestD = Infinity;
  for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) {
    const item = litterAt((ix + dx + 0.5) * STEP, (iz + dz + 0.5) * STEP,
                          siteAt, groundAt, worn);
    if (!item) continue;
    // a thing is "under the pointer" if the pointer is inside its own
    // footprint, or within the reach it was asked for — whichever is kinder
    const d = Math.hypot(item.x - x, item.z - z);
    if (d > Math.max(reach, item.size * 0.5)) continue;
    if (d < bestD) { bestD = d; best = item; }
  }
  return best;
}
